import { type FC, useState } from 'react';
import clsx from 'clsx';
import { useAppStore } from '@/stores/app-store';
import { computeScenarios, getRivalryBetween } from '@/services/country-conflicts';
import type { CountryProfile } from '@/types';

// ---------------------------------------------------------------------------
// Country Compare Modal — full side-by-side comparison + scenario simulation
// Appears when two countries are selected (Option A Step 3)
// ---------------------------------------------------------------------------

type Tab = 'overview' | 'military' | 'economy' | 'scenarios';

// ── Comparison bar ───────────────────────────────────────────────────────────

const CompareBar: FC<{
  label: string;
  val1: number; label1: string;
  val2: number; label2: string;
  fmt?: (v: number) => string;
  highlight?: boolean;
}> = ({ label, val1, label1, val2, label2, fmt, highlight }) => {
  const max = Math.max(val1, val2, 1);
  const pct1 = (val1 / max) * 100;
  const pct2 = (val2 / max) * 100;
  const f = fmt ?? ((v) => v.toLocaleString());
  const win1 = val1 > val2;
  const win2 = val2 > val1;
  return (
    <div className={clsx('py-2 border-b border-slate-800/30 last:border-0', highlight && 'bg-slate-800/20 rounded px-1')}>
      <div className="text-[9px] text-slate-500 uppercase tracking-wide mb-1.5">{label}</div>
      <div className="flex items-center gap-2">
        {/* Left value */}
        <div className="w-16 text-right shrink-0">
          <span className={clsx('text-[11px] font-bold', win1 ? 'text-sky-300' : 'text-slate-500')}>{f(val1)}</span>
          {win1 && <span className="ml-0.5 text-[8px] text-sky-500">▲</span>}
          <div className="text-[8px] text-slate-600 truncate">{label1.slice(0, 8)}</div>
        </div>

        {/* Center-out VS bars */}
        <div className="flex-1 flex h-4 rounded overflow-hidden gap-px">
          {/* Left half: sky-blue grows FROM center LEFTWARD (right-aligned) */}
          <div className="flex-1 flex items-center justify-end bg-slate-800/80 rounded-l overflow-hidden">
            <div
              className={clsx('h-full transition-all rounded-l', win1 ? 'bg-sky-400' : 'bg-sky-600/70')}
              style={{ width: `${pct1}%` }}
            />
          </div>
          {/* Center divider */}
          <div className="w-px bg-slate-600 shrink-0" />
          {/* Right half: rose grows FROM center RIGHTWARD (left-aligned) */}
          <div className="flex-1 flex items-center bg-slate-800/80 rounded-r overflow-hidden">
            <div
              className={clsx('h-full transition-all rounded-r', win2 ? 'bg-rose-400' : 'bg-rose-600/70')}
              style={{ width: `${pct2}%` }}
            />
          </div>
        </div>

        {/* Right value */}
        <div className="w-16 shrink-0">
          {win2 && <span className="mr-0.5 text-[8px] text-rose-500">▲</span>}
          <span className={clsx('text-[11px] font-bold', win2 ? 'text-rose-300' : 'text-slate-500')}>{f(val2)}</span>
          <div className="text-[8px] text-slate-600 truncate">{label2.slice(0, 8)}</div>
        </div>
      </div>
    </div>
  );
};

// ── Scenario card ────────────────────────────────────────────────────────────

const ScenarioCard: FC<{
  type: string; title: string; riskScore: number; riskLabel: string;
  keyFactors: string[]; outcome: string;
}> = ({ title, riskScore, riskLabel, keyFactors, outcome }) => {
  const [expanded, setExpanded] = useState(false);
  const color = riskScore >= 70 ? 'text-red-400 border-red-900/50 bg-red-950/20'
    : riskScore >= 50 ? 'text-orange-400 border-orange-900/50 bg-orange-950/20'
    : riskScore >= 30 ? 'text-yellow-400 border-yellow-900/50 bg-yellow-950/20'
    : 'text-emerald-400 border-emerald-900/50 bg-emerald-950/20';

  return (
    <div className={clsx('border rounded-lg p-2.5 cursor-pointer transition-all', color)}
      onClick={() => setExpanded((v) => !v)}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-bold">{title}</span>
        <div className="flex items-center gap-1.5">
          <div className="flex items-center gap-1">
            <div className="w-12 h-1.5 bg-slate-800 rounded-full overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${riskScore}%`,
                background: riskScore >= 70 ? '#ef4444' : riskScore >= 50 ? '#f97316' : riskScore >= 30 ? '#eab308' : '#10b981',
              }} />
            </div>
            <span className="text-[10px] font-bold">{riskScore}</span>
          </div>
          <span className="text-[8px] px-1 rounded bg-slate-800/60">{riskLabel}</span>
          {/* PREMIUM badge */}
          <span className="text-[7px] px-1 py-0.5 rounded bg-amber-900/60 text-amber-400 border border-amber-800/60 font-bold">PREMIUM</span>
          <span className="text-[9px] text-slate-500">{expanded ? '▲' : '▼'}</span>
        </div>
      </div>

      {expanded && (
        <div className="mt-2 space-y-1.5 border-t border-slate-800/40 pt-2">
          <div className="space-y-0.5">
            {keyFactors.map((f, i) => (
              <div key={i} className="text-[9px] text-slate-400 flex items-start gap-1">
                <span className="text-slate-600 shrink-0">·</span>{f}
              </div>
            ))}
          </div>
          <div className="text-[9px] text-slate-300 bg-slate-800/40 rounded p-1.5 leading-relaxed italic">
            {outcome}
          </div>
          <div className="text-[8px] text-amber-500/70 text-center">
            🔒 Full simulation requires Premium — upgrade to unlock
          </div>
        </div>
      )}
    </div>
  );
};

// ── Main modal ───────────────────────────────────────────────────────────────

export const CountryCompareModal: FC = () => {
  const selectedCountry = useAppStore((s) => s.selectedCountry);
  const compareCountry = useAppStore((s) => s.compareCountry);
  const clearCompare = useAppStore((s) => s.clearCompare);
  const [tab, setTab] = useState<Tab>('overview');

  if (!selectedCountry || !compareCountry) return null;

  const a: CountryProfile = selectedCountry;
  const b: CountryProfile = compareCountry;
  const rivalry = getRivalryBetween(a.code, b.code);
  const scenarios = computeScenarios(a, b);

  const fgdp = (v: number) => `$${v >= 1000 ? `${(v/1000).toFixed(1)}T` : `${v}B`}`;
  const fpop = (v: number) => `${v >= 1000 ? `${(v/1000).toFixed(1)}B` : `${v}M`}`;

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'military', label: 'Military' },
    { id: 'economy',  label: 'Economy' },
    { id: 'scenarios', label: '⚡ Scenarios' },
  ];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center pointer-events-none">
      <div
        className="pointer-events-auto relative w-[calc(100%-2rem)] max-w-2xl max-h-[85vh] flex flex-col bg-gray-950/98 backdrop-blur-xl border border-slate-700/60 rounded-2xl shadow-2xl overflow-hidden"
        style={{ animation: 'slideUp 200ms ease-out' }}
      >
        {/* Header */}
        <div className="flex items-center gap-2 p-3 md:p-4 border-b border-slate-800/60 shrink-0">
          {/* Country A */}
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="text-xl md:text-2xl shrink-0">{a.flagEmoji}</span>
            <div className="min-w-0">
              <div className="text-[11px] md:text-[12px] font-bold text-sky-300 truncate">{a.name}</div>
              <div className="text-[8px] md:text-[9px] text-slate-500">Pwr {a.powerIndex}</div>
            </div>
          </div>
          {/* VS badge */}
          <div className="flex-1 flex flex-col items-center min-w-0 shrink">
            {rivalry ? (
              <span className={clsx(
                'text-[8px] md:text-[9px] font-bold px-1.5 py-0.5 rounded border whitespace-nowrap',
                rivalry.intensity === 'critical' ? 'bg-red-950/60 text-red-400 border-red-800'
                : rivalry.intensity === 'high' ? 'bg-orange-950/60 text-orange-400 border-orange-800'
                : 'bg-slate-800 text-slate-400 border-slate-700',
              )}>
                {rivalry.intensity.toUpperCase()} RIVALRY
              </span>
            ) : (
              <span className="text-[9px] text-slate-500 border border-slate-800 rounded px-2 py-1">NO FORMAL CONFLICT</span>
            )}
            {rivalry && (
              <div className="text-[8px] text-slate-600 mt-0.5 text-center max-w-32 truncate">
                {rivalry.description.slice(0, 50)}...
              </div>
            )}
          </div>
          {/* Country B */}
          <div className="flex items-center gap-1.5 min-w-0">
            <div className="text-right min-w-0">
              <div className="text-[11px] md:text-[12px] font-bold text-rose-300 truncate">{b.name}</div>
              <div className="text-[8px] md:text-[9px] text-slate-500">Pwr {b.powerIndex}</div>
            </div>
            <span className="text-xl md:text-2xl shrink-0">{b.flagEmoji}</span>
          </div>
          {/* Close */}
          <button
            onClick={clearCompare}
            className="ml-1 shrink-0 text-slate-500 hover:text-slate-200 transition-colors text-[14px] w-6 h-6 flex items-center justify-center rounded hover:bg-slate-800"
          >✕</button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-3 md:px-4 pt-2 shrink-0 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={clsx('px-3 py-1 rounded text-[10px] font-medium transition-colors border',
                tab === t.id
                  ? 'bg-slate-700 text-slate-100 border-slate-600'
                  : 'text-slate-500 border-slate-800 hover:text-slate-300',
              )}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 py-3 scrollbar-thin min-h-0">

          {/* ── Overview ── */}
          {tab === 'overview' && (
            <div className="space-y-0.5">
              <CompareBar label="Global Power Index" val1={a.powerIndex} label1={a.name} val2={b.powerIndex} label2={b.name} highlight />
              <CompareBar label="Population" val1={a.population} label1={a.name} val2={b.population} label2={b.name} fmt={fpop} />
              <CompareBar label="GDP" val1={a.gdpBillionUSD} label1={a.name} val2={b.gdpBillionUSD} label2={b.name} fmt={fgdp} />
              <CompareBar label="GDP per Capita" val1={a.gdpPerCapita} label1={a.name} val2={b.gdpPerCapita} label2={b.name}
                fmt={(v) => `$${(v/1000).toFixed(0)}K`} />
              <CompareBar label="GDP Growth %" val1={a.gdpGrowthPct} label1={a.name} val2={b.gdpGrowthPct} label2={b.name}
                fmt={(v) => `${v}%`} />

              {/* Geopolitical row */}
              <div className="pt-2 grid grid-cols-2 gap-3">
                {[a, b].map((c, i) => (
                  <div key={i} className={clsx('rounded-lg p-2.5 border', i === 0 ? 'border-sky-900/40 bg-sky-950/10' : 'border-rose-900/40 bg-rose-950/10')}>
                    <div className="text-[9px] font-bold uppercase tracking-wide text-slate-500 mb-1.5">{c.name}</div>
                    <div className="space-y-1">
                      <div className="flex justify-between text-[9px]">
                        <span className="text-slate-500">Stance</span>
                        <span className="text-slate-300 capitalize">{c.geopoliticalStance}</span>
                      </div>
                      <div className="flex justify-between text-[9px]">
                        <span className="text-slate-500">Nuclear</span>
                        <span className={c.nuclearStatus === 'possesses' ? 'text-yellow-400' : 'text-slate-400'}>
                          {c.nuclearStatus === 'possesses' ? '☢ Confirmed' : c.nuclearStatus}
                        </span>
                      </div>
                      <div className="flex justify-between text-[9px]">
                        <span className="text-slate-500">Leader risk</span>
                        <span className={c.leader.riskLevel === 'critical' ? 'text-red-400' : c.leader.riskLevel === 'high' ? 'text-orange-400' : 'text-emerald-400'}>
                          {c.leader.riskLevel.toUpperCase()}
                        </span>
                      </div>
                      <div className="text-[8px] text-slate-600 mt-1">{c.alliances.slice(0,4).join(' · ')}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Rivalry details */}
              {rivalry && (
                <div className="mt-2 border border-red-900/40 rounded-lg p-2.5 bg-red-950/10">
                  <div className="text-[9px] font-bold text-red-400 uppercase tracking-wide mb-1">Active Rivalry Intelligence</div>
                  <p className="text-[9px] text-slate-400 mb-1.5">{rivalry.description}</p>
                  <div className="flex flex-wrap gap-1 mb-1.5">
                    {rivalry.type.map((t) => (
                      <span key={t} className="text-[8px] px-1.5 rounded bg-slate-800 text-slate-400">{t}</span>
                    ))}
                    <span className={clsx('text-[8px] px-1.5 rounded font-bold', rivalry.activeMilitary ? 'bg-red-950/60 text-red-400' : 'bg-slate-800 text-slate-500')}>
                      {rivalry.activeMilitary ? '⚔ ACTIVE MILITARY' : 'Non-kinetic'}
                    </span>
                  </div>
                  <div className="space-y-0.5">
                    {rivalry.keyIssues.map((issue, i) => (
                      <div key={i} className="text-[9px] text-slate-400 flex gap-1">
                        <span className="text-slate-600">·</span>{issue}
                      </div>
                    ))}
                  </div>
                  {rivalry.latestIntel && (
                    <div className="mt-1.5 text-[9px] text-amber-400/80 bg-amber-950/20 rounded p-1.5 border border-amber-900/40 italic">
                      Intel: {rivalry.latestIntel}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Military ── */}
          {tab === 'military' && (() => {
            const aHasData = a.military.activeTroops > 0 || a.military.combatAircraft > 0;
            const bHasData = b.military.activeTroops > 0 || b.military.combatAircraft > 0;
            return (
              <div className="space-y-0.5">
                {!aHasData && !bHasData ? (
                  <div className="py-8 text-center text-slate-500 text-[11px]">
                    <div className="text-2xl mb-2">🔒</div>
                    Military data not publicly tracked for either country.<br/>
                    <span className="text-slate-600 text-[9px]">Limited coverage outside G20 / NATO / major powers.</span>
                  </div>
                ) : (
                  <>
                    {(!aHasData || !bHasData) && (
                      <div className="text-[9px] text-slate-600 italic mb-1 text-center">
                        * {!aHasData ? a.name : b.name}: military data not publicly tracked
                      </div>
                    )}
                    <CompareBar label="Military Budget ($B)" val1={a.military.budgetBillionUSD} label1={a.name} val2={b.military.budgetBillionUSD} label2={b.name} highlight />
                    <CompareBar label="Active Troops (K)" val1={a.military.activeTroops} label1={a.name} val2={b.military.activeTroops} label2={b.name}
                      fmt={(v) => v >= 1000 ? `${(v/1000).toFixed(1)}M` : `${v}K`} />
                    <CompareBar label="Combat Aircraft" val1={a.military.combatAircraft} label1={a.name} val2={b.military.combatAircraft} label2={b.name} />
                    <CompareBar label="Aircraft Carriers" val1={a.military.aircraftCarriers} label1={a.name} val2={b.military.aircraftCarriers} label2={b.name} />
                    <CompareBar label="Submarines" val1={a.military.submarines} label1={a.name} val2={b.military.submarines} label2={b.name} />
                    <CompareBar label="Destroyers/Frigates" val1={a.military.destroyersFrigates} label1={a.name} val2={b.military.destroyersFrigates} label2={b.name} />
                    <CompareBar label="Tanks" val1={a.military.tanks} label1={a.name} val2={b.military.tanks} label2={b.name} />
                    {(a.military.nuclearWarheads || b.military.nuclearWarheads) && (
                      <CompareBar label="Nuclear Warheads ☢" val1={a.military.nuclearWarheads ?? 0} label1={a.name} val2={b.military.nuclearWarheads ?? 0} label2={b.name} highlight />
                    )}
                    {a.military.globalFirepowerRank && b.military.globalFirepowerRank && (
                      <div className="pt-2 flex gap-3">
                        {[a, b].map((c, i) => (
                          <div key={i} className={clsx('flex-1 rounded p-2 text-center border', i === 0 ? 'border-sky-900/40' : 'border-rose-900/40')}>
                            <div className={clsx('text-[16px] font-bold', i === 0 ? 'text-sky-400' : 'text-rose-400')}>
                              #{c.military.globalFirepowerRank}
                            </div>
                            <div className="text-[8px] text-slate-500">GFP Rank</div>
                            <div className="text-[9px] text-slate-400">{c.name}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })()}

          {/* ── Economy ── */}
          {tab === 'economy' && (
            <div className="space-y-0.5">
              <CompareBar label="GDP (Total)" val1={a.gdpBillionUSD} label1={a.name} val2={b.gdpBillionUSD} label2={b.name} fmt={fgdp} highlight />
              <CompareBar label="GDP per Capita" val1={a.gdpPerCapita} label1={a.name} val2={b.gdpPerCapita} label2={b.name}
                fmt={(v) => `$${(v/1000).toFixed(1)}K`} />
              <CompareBar label="GDP Growth %" val1={a.gdpGrowthPct} label1={a.name} val2={b.gdpGrowthPct} label2={b.name}
                fmt={(v) => `${v > 0 ? '+' : ''}${v}%`} />
              <CompareBar label="Military Budget ($B)" val1={a.military.budgetBillionUSD} label1={a.name} val2={b.military.budgetBillionUSD} label2={b.name} />
              {/* Defense burden */}
              <CompareBar label="Defense / GDP (%)" val1={+(a.military.budgetBillionUSD / a.gdpBillionUSD * 100).toFixed(2)}
                label1={a.name} val2={+(b.military.budgetBillionUSD / b.gdpBillionUSD * 100).toFixed(2)} label2={b.name}
                fmt={(v) => `${v.toFixed(1)}%`} />
              <CompareBar label="Population" val1={a.population} label1={a.name} val2={b.population} label2={b.name} fmt={fpop} />

              {/* GDP Sectors if available */}
              {(a.gdpSectors || b.gdpSectors) && (
                <div className="pt-2 grid grid-cols-2 gap-3">
                  {[a, b].map((c, i) => c.gdpSectors ? (
                    <div key={i} className={clsx('rounded-lg p-2 border', i === 0 ? 'border-sky-900/40' : 'border-rose-900/40')}>
                      <div className="text-[9px] font-bold text-slate-400 mb-1">{c.name} — GDP Sectors</div>
                      {c.gdpSectors.map((s) => (
                        <div key={s.name} className="flex items-center gap-1.5 mb-0.5">
                          <div className="flex-1 h-1 bg-slate-800 rounded overflow-hidden">
                            <div className="h-full bg-slate-500 rounded" style={{ width: `${s.pct}%` }} />
                          </div>
                          <span className="text-[8px] text-slate-400 w-20 truncate">{s.name}</span>
                          <span className="text-[8px] text-slate-500 w-8 text-right">{s.pct}%</span>
                        </div>
                      ))}
                    </div>
                  ) : null)}
                </div>
              )}
            </div>
          )}

          {/* ── Scenarios ── */}
          {tab === 'scenarios' && (
            <div className="space-y-2">
              <div className="text-[9px] text-slate-500 pb-1 border-b border-slate-800/40">
                AI-powered scenario simulation based on current geopolitical data.
                Risk scores computed from military power, alliances, nuclear status, and historical relations.
              </div>
              {scenarios.map((s) => (
                <ScenarioCard key={s.type} {...s} />
              ))}
              <div className="text-center pt-2">
                <div className="inline-flex items-center gap-2 text-[9px] text-amber-500/70 bg-amber-950/20 border border-amber-900/40 rounded-lg px-3 py-2">
                  🔒 Full simulation (timeline, force deployments, economic cascade) — DetectX Quasar Premium
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
